import React, { useState, useMemo } from 'react';
// Fix: Replaced Playlist icon with ViewList to resolve import conflicts
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  Chip,
  IconButton,
  Tooltip,
  Stack,
  Divider,
  Alert,
  CircularProgress,
  Badge,
  Popover,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  useTheme,
  alpha,
} from '@mui/material';
import {
  DateCalendar,
} from '@mui/x-date-pickers/DateCalendar';
import {
  PickersDay,
  type PickersDayProps,
} from '@mui/x-date-pickers/PickersDay';
import {
  LocalizationProvider,
} from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  PlayArrow,
  Schedule as ScheduleIcon,
  Monitor,
  Event,
  Person,
  ViewList as PlaylistIcon,
} from '@mui/icons-material';
import { format, isSameDay, isToday, startOfMonth, endOfMonth } from 'date-fns';
import { ru, enUS } from 'date-fns/locale';

import {
  EventStatus,
  Priority
} from '../../types/index';
import type {
  CalendarEvent,
  ScheduleStatus
} from '../../types/index';
import apiClient from '../../services/api';

interface ScheduleCalendarViewProps {
  filters: {
    search?: string;
    device_id?: number;
    playlist_id?: number;
    status?: ScheduleStatus;
    priority?: Priority[];
    start_date?: string;
    end_date?: string;
  };
}

interface CalendarDayProps extends PickersDayProps<Date> {
  events: CalendarEvent[];
  isSelected: boolean;
  isToday: boolean;
}

const CalendarDay: React.FC<CalendarDayProps> = (props) => {
  const { events, isSelected, isToday, day, ...other } = props;
  const theme = useTheme();

  const hasEvents = events.length > 0;
  const activeEvents = events.filter(event => event.status === 'active').length;
  const pendingEvents = events.filter(event => event.status === 'scheduled').length;

  return (
    <Badge
      key={day.toString()}
      overlap="circular"
      badgeContent={hasEvents ? events.length : undefined}
      color={activeEvents > 0 ? 'primary' : pendingEvents > 0 ? 'warning' : 'default'}
      sx={{
        '& .MuiBadge-badge': {
          fontSize: '10px',
          height: '16px',
          minWidth: '16px',
        },
      }}
    >
      <PickersDay
        {...other}
        day={day}
        sx={{
          ...(hasEvents && {
            backgroundColor: alpha(theme.palette.primary.main, 0.1),
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.2),
            },
          }),
          ...(isToday && {
            fontWeight: 'bold',
            border: `2px solid ${theme.palette.primary.main}`,
          }),
          ...(isSelected && {
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            '&:hover': {
              backgroundColor: theme.palette.primary.dark,
            },
          }),
        }}
      />
    </Badge>
  );
};

const EventStatusIcon: React.FC<{ status: EventStatus }> = ({ status }) => {
  switch (status) {
    case 'active':
      return <PlayArrow color="success" fontSize="small" />;
    case 'scheduled':
      return <ScheduleIcon color="warning" fontSize="small" />;
    case 'completed':
      return <Event color="action" fontSize="small" />;
    case 'cancelled':
      return <Event color="error" fontSize="small" />;
    default:
      return <Event color="action" fontSize="small" />;
  }
};

const PriorityChip: React.FC<{ priority: Priority }> = ({ priority }) => {
  const { t } = useTranslation();

  const getPriorityColor = (p: Priority): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    switch (p) {
      case 20: return 'error';    // Critical
      case 15: return 'warning';  // High
      case 10: return 'primary';  // Normal
      case 5: return 'info';      // Low
      case 1: return 'default';   // Very Low
      default: return 'default';
    }
  };

  const getPriorityLabel = (p: Priority): string => {
    switch (p) {
      case 20: return t('common.priority.critical');
      case 15: return t('common.priority.high');
      case 10: return t('common.priority.normal');
      case 5: return t('common.priority.low');
      case 1: return t('common.priority.veryLow');
      default: return t('common.priority.normal');
    }
  };

  return (
    <Chip
      label={getPriorityLabel(priority)}
      color={getPriorityColor(priority)}
      size="small"
      variant="outlined"
    />
  );
};

const ScheduleCalendarView: React.FC<ScheduleCalendarViewProps> = ({ filters }) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const dateLocale = i18n.language === 'ru' ? ru : enUS;

  // Получаем события календаря
  const { data: calendarData, isLoading, error } = useQuery({
    queryKey: ['calendarEvents', {
      start_date: format(startOfMonth(selectedDate), 'yyyy-MM-dd'),
      end_date: format(endOfMonth(selectedDate), 'yyyy-MM-dd'),
      ...filters,
    }],
    queryFn: () => apiClient.getCalendarEvents(
      filters.start_date || format(startOfMonth(selectedDate), 'yyyy-MM-dd'),
      filters.end_date || format(endOfMonth(selectedDate), 'yyyy-MM-dd'),
      filters.device_id ? [filters.device_id] : undefined,
    ),
  });

  const events = calendarData?.events || [];

  // Группируем события по датам
  const eventsByDate = useMemo(() => {
    const grouped: Record<string, CalendarEvent[]> = {};
    events.forEach(event => {
      const dateKey = format(new Date(event.start_time), 'yyyy-MM-dd');
      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }
      grouped[dateKey].push(event);
    });
    return grouped;
  }, [events]);

  // События для выбранной даты
  const selectedDateEvents = useMemo(() => {
    const dateKey = format(selectedDate, 'yyyy-MM-dd');
    return eventsByDate[dateKey] || [];
  }, [eventsByDate, selectedDate]);

  const handleDateChange = (date: Date | null) => {
    if (date) {
      setSelectedDate(date);
    }
  };

  const handleDayClick = (event: React.MouseEvent<HTMLElement>, date: Date) => {
    const dateKey = format(date, 'yyyy-MM-dd');
    const dayEvents = eventsByDate[dateKey] || [];

    if (dayEvents.length > 0) {
      setAnchorEl(event.currentTarget);
      setSelectedDate(date);
    }
  };

  const renderDay = (day: Date, _selectedDays: Array<Date | null>, pickersDayProps: PickersDayProps<Date>) => {
    const dateKey = format(day, 'yyyy-MM-dd');
    const dayEvents = eventsByDate[dateKey] || [];

    return (
      <CalendarDay
        {...pickersDayProps}
        events={dayEvents}
        isSelected={isSameDay(day, selectedDate)}
        isToday={isToday(day)}
        onClick={(event) => handleDayClick(event, day)}
      />
    );
  };

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {t('common.loadError')}
      </Alert>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={dateLocale}>
      <Box sx={{ display: 'flex', height: '600px', gap: 2 }}>
        {/* Календарь */}
        <Paper elevation={1} sx={{ flex: 1, p: 2 }}>
          <Typography variant="h6" gutterBottom>
            {t('schedule.calendarView')}
          </Typography>

          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <DateCalendar
              value={selectedDate}
              onChange={handleDateChange}
              slots={{
                day: renderDay,
              }}
              sx={{
                width: '100%',
                '& .MuiPickersCalendarHeader-root': {
                  paddingX: 2,
                },
                '& .MuiDayCalendar-weekDayLabel': {
                  fontWeight: 600,
                },
              }}
            />
          )}
        </Paper>

        {/* Боковая панель с событиями */}
        <Paper elevation={1} sx={{ width: 400, p: 2 }}>
          <Stack spacing={2}>
            <Typography variant="h6">
              {format(selectedDate, 'dd MMMM yyyy', { locale: dateLocale })}
            </Typography>

            <Divider />

            {selectedDateEvents.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body2" color="text.secondary">
                  {t('schedule.noEventsOnDate')}
                </Typography>
              </Box>
            ) : (
              <List dense>
                {selectedDateEvents.map((event) => (
                  <Card key={event.id} variant="outlined" sx={{ mb: 1 }}>
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                      <Stack spacing={1}>
                        {/* Заголовок события */}
                        <Stack direction="row" spacing={1} alignItems="center">
                          <EventStatusIcon status={event.status} />
                          <Typography variant="subtitle2" sx={{ flex: 1 }}>
                            {event.title}
                          </Typography>
                          <PriorityChip priority={event.priority} />
                        </Stack>

                        {/* Время */}
                        <Typography variant="caption" color="text.secondary">
                          {format(new Date(event.start_time), 'HH:mm')} - {format(new Date(event.end_time), 'HH:mm')}
                        </Typography>

                        {/* Устройство */}
                        {event.device && (
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Monitor fontSize="small" color="action" />
                            <Typography variant="caption">
                              {event.device.name}
                            </Typography>
                          </Stack>
                        )}

                        {/* Плейлист */}
                        {event.playlist && (
                          <Stack direction="row" spacing={1} alignItems="center">
                            <PlaylistIcon fontSize="small" color="action" />
                            <Typography variant="caption">
                              {event.playlist.name}
                            </Typography>
                          </Stack>
                        )}

                        {/* Автор */}
                        {event.author && (
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Person fontSize="small" color="action" />
                            <Typography variant="caption">
                              {event.author.full_name}
                            </Typography>
                          </Stack>
                        )}

                        {/* Описание */}
                        {event.description && (
                          <Typography variant="caption" color="text.secondary">
                            {event.description}
                          </Typography>
                        )}
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </List>
            )}
          </Stack>
        </Paper>

        {/* Popover для быстрого просмотра событий дня */}
        <Popover
          open={Boolean(anchorEl) && selectedDateEvents.length > 0}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
        >
          <Box sx={{ p: 2, maxWidth: 300 }}>
            <Typography variant="subtitle2" gutterBottom>
              {format(selectedDate, 'dd MMMM', { locale: dateLocale })}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('schedule.eventsCount', { count: selectedDateEvents.length })}
            </Typography>
          </Box>
        </Popover>
      </Box>
    </LocalizationProvider>
  );
};

export default ScheduleCalendarView;
